<template src="./template.html"></template>
<script>
import _extend from 'lodash/extend'
import moment from 'moment'
import { mapState } from 'vuex'
import Datepicker from 'vuejs-datepicker'

import admissionGroupRequestService from 'api/admissionGroupRequest'
import daySettingService from 'api/daySetting'
import dateUtils from 'utils/dateUtils'
import VENUE_TYPES from 'constants/venueTypes'
import analyticsLib from 'lib/analytics'
import TRACKING_EVENTS from 'constants/trackingEvents'
import storageLib from 'lib/storage'

function generateArrivalTimes(start, end) {
  const arrivalTimes = []

  for (let i = start; i <= end; i += 1800000) {
    arrivalTimes.push(i)
  }

  return arrivalTimes
}

export default {
  name: 'add-admission-group',
  components: {
    Datepicker,
  },
  props: {
    type: {
      type: String,
      default: 'TABLE',
      required: true,
    },
    hidePoweredBy: {
      type: Boolean,
      default: false,
    },
    hideGuestCount: {
      type: Boolean,
      default: false,
    },
    shouldLockDate: {
      type: Boolean,
      default: false,
    },
    buttonBgColor: {
      type: String,
    },
    buttonTextColor: {
      type: String,
    },
    buttonText: {
      type: String,
      default: 'Submit',
    },
    theme: {
      type: String,
      default: 'theme-light',
    },
  },
  data() {
    return {
      saving: false,
      venueClosed: false,
      admissionGroupCreated: false,
      admissionGroupError: null,
      guestsCounts: this.type === 'TABLE' ? Array.from(Array(100).keys()).map((key) => key + 1) : Array.from(Array(10).keys()).map((key) => key + 1),
      datePickerDisabledOptions: {
        to: moment.utc().subtract(1, 'day').toDate(),
      },
      arrivalTimes: [],
      addAdmissionGroupForm: {
        date: new Date(),
        arrivalTime: null,
        birthday: null,
        name: null,
        email: null,
        phone: null,
        notes: null,
        guestsExpected: 0,
        guestsExpectedMale: null,
        guestsExpectedFemale: null,
        customerMarketingOptIn: false,
        customerMarketingOptInSignature: null,
      },
      formViewTrackSent: false,
    }
  },
  watch: {
    addAdmissionGroupForm: {
      deep: true,
      handler() {
        this.trackFormViewed()
      },
    },
  },
  computed: {
    ...mapState({
      date: (state) => state.date.current,
      venue: (state) => state.venue.current,
      inventory: (state) => state.inventory.current,
      daySettings: (state) => state.daySettings.current,
      //
      attributionId: (state) => state.config.attributionId,
    }),
    buttonBgColorSanitized() {
      if (!this.buttonBgColor) return null

      if (!this.buttonBgColor.startsWith('#')) return `#${this.buttonBgColor}`

      return this.buttonBgColor
    },
    buttonTextColorSanitized() {
      if (!this.buttonTextColor) return null

      if (!this.buttonTextColor.startsWith('#')) return `#${this.buttonTextColor}`

      return this.buttonTextColor
    },
    acceptingGuestlistExternally() {
      return this.daySettings && this.daySettings.acceptingGuestlistExternally
    },
    acceptingReservationRequests() {
      return this.daySettings && this.daySettings.acceptingReservationRequests
    },
    requireSignature() {
      return this.venue.type === VENUE_TYPES.DISPENSARY
    },
    showPhoneField() {
      if (!this.venue) return true

      return ![].includes(this.venue.id)
    },
    showBirthdayField() {
      if (!this.venue) return true

      return !['d33a8bf3fdb3fb2e', '018129ce4c6cb12f'].includes(this.venue.id)
    },
  },
  methods: {
    hideAlerts() {
      this.admissionGroupCreated = false
      this.admissionGroupError = null

      this.resetForm()
    },
    resetForm() {
      this.addAdmissionGroupForm = {
        date: new Date(),
        name: null,
        email: null,
        phone: null,
        notes: null,
        guestsExpected: 0,
        guestsExpectedMale: null,
        guestsExpectedFemale: null,
        customerMarketingOptIn: false,
        customerMarketingOptInSignature: null,
      }
    },
    saveAdmissionGroup() {
      this.$validator.validateAll().then((success) => {
        if (success) {
          this.saving = true

          const data = _extend({}, this.addAdmissionGroupForm)
          data.venueId = this.venue.id
          data.type = this.type

          if (data.arrivalTime) {
            const date = dateUtils.toUTCMomentWithoutTime(data.date)
            const arrivalTime = moment.utc(data.arrivalTime, 'x')

            data.arrivalTime = date.hour(arrivalTime.hour()).minute(arrivalTime.minute())
          }

          if (this.attributionId) {
            data.attributionId = this.attributionId
          }

          data.date = dateUtils.toUTCTimestampWithoutTime(data.date)

          if (data.birthday) {
            const birthday = moment(data.birthday, 'MM/DD/YYYY')
            data.birthdayYear = birthday.get('year')
            data.birthdayMonth = birthday.get('month') + 1
            data.birthdayDay = birthday.get('date')
          }

          if (this.type !== 'TABLE') {
            data.guestsExpected += 1
          }

          admissionGroupRequestService
            .create(data)
            .then((result) => {
              this.admissionGroupCreated = true

              this.$emit('admissionGroupCreated', result)

              const trackingEvent = this.type === 'TABLE' ? TRACKING_EVENTS.ReservationRequested : TRACKING_EVENTS.GuestlistRequested

              analyticsLib.trackEvent(trackingEvent, {
                admissionGroupId: result.id,
              })

              //start a new session
              storageLib.setSessionToken()
            })
            .catch((err) => {
              this.saving = false
              this.admissionGroupError = (err || {}).message
            })
        }
      })
    },
    trackFormViewed() {
      if (this.formViewTrackSent) return

      const trackingEvent = this.type === 'TABLE' ? TRACKING_EVENTS.ViewedReservationRequest : TRACKING_EVENTS.ViewedGuestistRequest

      analyticsLib.trackEvent(trackingEvent, {
        venue: this.venue.id,
      })

      this.formViewTrackSent = true
    },
    onDatepickerOpened() {
      this.$emit('datePickerOpened')
    },
    onDatepickerClosed() {
      this.$emit('updated')
    },
    onDatepickerSelected(date) {
      this.addAdmissionGroupForm.date = date

      this.getDaySettings()
    },
    onIframeBlurred() {
      if (this.$refs.datePicker) {
        this.$refs.datePicker.close()
      }
    },
    getDaySettings() {
      return daySettingService.getByIdAndVenueId(dateUtils.toUTCTimestampWithoutTime(this.addAdmissionGroupForm.date), this.venue.id).then((result) => {
        this.$store.dispatch('setCurrentDaySettings', result.data)
      })
    },
  },
  created() {
    if (this.shouldLockDate) {
      this.addAdmissionGroupForm.date = new Date(this.date.get('year'), this.date.get('month'), this.date.get('date'), 0, 0, 0)
    }

    this.arrivalTimes = generateArrivalTimes(moment.utc().startOf('date').toDate().getTime(), moment.utc().endOf('day').toDate().getTime())

    if (!this.daySettings) {
      this.getDaySettings()
    }

    if (this.type !== 'TABLE') {
      if (this.type !== 'TABLE' && this.venue.config && this.venue.config.guestlistMax > 0) {
        this.guestsCounts = []

        //subtract 1 because guest count is for additional guests (name is 1 guest)
        for (let i = 0; i < this.venue.config.guestlistMax - 1; i++) {
          //eslint-disable-line
          //eslint-disable-line
          this.guestsCounts.push(i + 1)
        }
      }
    }
  },
  updated() {
    this.$emit('updated')
  },
}
</script>



// WEBPACK FOOTER //
// src/components/AddAdmissionGroup/index.vue